import React, { useState } from "react";
import { Copy, Check, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useRegistry } from "@/context/RegistryContext";
import { publishRegistry } from "@/lib/api";

export const PublishButton = () => {
  const { profile, registry } = useRegistry();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const canPublish = registry.length > 0;

  const publish = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await publishRegistry({
        profile,
        items: registry.map((r) => r.item),
      });
      const url = `${window.location.origin}/r/${data.slug}`;
      setLink(url);
      setOpen(true);
    } catch (e) {
      setErr("Could not publish. Please try again.");
    }
    setLoading(false);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (!canPublish) return null;
  return (
    <>
      <button
        data-testid="publish-registry-btn"
        onClick={publish}
        disabled={loading}
        className="rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] px-6 py-3 text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-60"
      >
        <Share2 size={16} strokeWidth={1.5} />
        {loading ? "Publishing…" : "Publish & Get Shareable Link"}
      </button>
      {err && <span className="ml-3 text-xs text-[#D98A71]">{err}</span>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="publish-success-modal"
          className="bg-[#FDFBF7] border-[#E5E1D8] rounded-3xl max-w-md p-8"
        >
          <DialogHeader>
            <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">Published</span>
            <DialogTitle className="font-display text-3xl text-[#2D3732] mt-3 leading-tight">
              Her registry is<br /><span className="italic text-[#D98A71]">out in the world.</span>
            </DialogTitle>
            <DialogDescription className="text-[#5C6B62] mt-3">
              Share this link with friends and family. It opens straight into her guest view.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 rounded-2xl border border-[#E5E1D8] bg-white/70 p-4 flex items-center gap-3">
            <span data-testid="share-link" className="text-sm text-[#2D3732] flex-1 truncate">{link}</span>
            <button
              data-testid="copy-link-btn"
              onClick={copy}
              className="rounded-full bg-[#2D3732] hover:bg-[#1e2521] text-[#FDFBF7] px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              {copied ? <><Check size={14} />Copied</> : <><Copy size={14} />Copy</>}
            </button>
          </div>

          {link && (
            <div
              data-testid="share-qr"
              className="mt-6 rounded-2xl border border-[#E5E1D8] bg-white/70 p-6 flex flex-col items-center"
            >
              <div className="p-3 bg-white rounded-xl">
                <QRCodeSVG
                  value={link}
                  size={168}
                  level="M"
                  fgColor="#2D3732"
                  bgColor="#FFFFFF"
                />
              </div>
              <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] text-center">
                Scan to open her registry
              </p>
              <p className="mt-1 text-xs text-[#5C6B62] text-center">
                Perfect for baby-shower invites and printed cards.
              </p>
            </div>
          )}

          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="mt-4 block text-center text-xs tracking-[0.15em] uppercase text-[#5C6B62] hover:text-[#2D3732] transition-colors"
          >
            Open guest view →
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
};
